import { AVALANCHE_SLOPES } from '@somnhold/content';
import { hexDist, hexLen, hexToWorld, ROW, worldToHex, type Hex } from '../hex.ts';
import { idiv, imin } from '../math/fixed.ts';
import { deriveSeed, rngInt, rngRange, seedRng } from '../math/rng.ts';
import { cell, colOf, slopeLayout } from './fort-layout.ts';
import { bigWedges, LANES, type LaneRock } from './wedge-layout.ts';

/**
 * The avalanche's grounds (docs/sendas/guerrero.md, chapter 4, «El alud»): the same from the seed in the sim and the
 * drawing, integers only. Up the slope is −r (the top of the screen); the snow comes down toward the camera.
 *
 * - `looseLayout` (act 1, the loose slope): the icy climb's trail (`slopeLayout`, chapter 3) with boulders on it for
 *   cover, a pair every few rows, and the ice cave's mouth at its top where the fort's door was; the small giant asleep
 *   beside the trail's upper half. The lee of a boulder (the cells straight below it) is where a front misses.
 * - `avalancheLayout` (act 3, the test): an open slope seven lanes wide down to the cave's mouth at its foot, the void
 *   either side; the nine rocks of the large wedges in three rows (`bigWedges`, from the content's table); the line the
 *   giant walks across; the edges the wolves come over.
 * - `caveLayout` (act 2): inside the ice, three rooms one above the other joined by narrow ways, the ice of each way
 *   shut until the room below it is cleared; past the third, the hall where Fermín and Juana sit.
 */

export const LOOSE_RADIUS = 9;
export const AVALANCHE_RADIUS = 9;
export const CAVE_RADIUS = 9;

const allCells = (R: number): Hex[] => {
  const out: Hex[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

const key = (h: Hex): string => `${h.q},${h.r}`;

/** The cells straight below a rock (a front coming down misses them): its two downhill neighbours. */
export const leeOf = (h: Hex): Hex[] => [
  { q: h.q, r: h.r + 1 },
  { q: h.q - 1, r: h.r + 1 },
];

export interface LooseLayout {
  radius: number;
  /** What can't be walked: the trail's sides, the boulders, the ice above the mouth. */
  rocks: Hex[];
  /** The boulders on the trail (each shelters the cells below it). */
  cover: Hex[];
  /** The trail's middle row by row, foot to mouth (x2 half-hexes and row, as the climb's). */
  way: { x2: number; r: number }[];
  mid: number[];
  /** The cave's mouth (the way's top), where the giant sleeps, where the wolves come down from. */
  mouth: { x2: number; r: number };
  giant: Hex;
  edges: Hex[];
}

/** The loose slope: the climb's trail, boulders for cover on it, the cave's mouth at the top. */
export const looseLayout = (seed: number): LooseLayout => {
  const S = slopeLayout(seed);
  const R = S.radius;
  const M = R + 2;
  const rng = seedRng(deriveSeed(seed, 0x10f5));
  const rock = new Set(S.rocks.map(key));
  const cover: Hex[] = [];
  // A pair of boulders every third row from the foot, either side of the trail's middle (two half-hexes clear between
  // them: Kel's way stays open), staggered so one side or the other is always near.
  for (let r = R - 3; r >= -(R - 3); r -= 3) {
    const m = S.mid[r + M] as number;
    const off = 3 + rngInt(rng, 2);
    for (const side of [-1, 1]) {
      const x2 = m + side * (off + (side > 0 ? rngInt(rng, 2) : 0));
      const h = cell(x2 + ((x2 ^ r) & 1), r);
      if (rock.has(key(h)) || hexLen(h.q, h.r) > R - 1) continue;
      rock.add(key(h));
      cover.push(h);
    }
  }
  // The giant's bed: beside the trail, a little past halfway up, on the side with room.
  const gr = -rngRange(rng, 2, 4);
  const gm = S.mid[gr + M] as number;
  const gside = gm > 0 ? -1 : 1;
  // At the trail's edge (as far from its middle as the open ground goes there), never on Kel's way.
  let giant = cell(gm + ((gm ^ gr) & 1), gr);
  for (const off of [5, 4, 3]) {
    const gx = gm + gside * off;
    const c = cell(gx + ((gx ^ gr) & 1), gr);
    if (!rock.has(key(c)) && hexLen(c.q, c.r) <= R - 1) {
      giant = c;
      break;
    }
  }
  const rocks = [...rock].map((k) => {
    const [q, r] = k.split(',');
    return { q: Number(q), r: Number(r) };
  });
  rocks.sort((a, b) => a.r - b.r || a.q - b.q);
  cover.sort((a, b) => a.r - b.r || a.q - b.q);
  return { radius: R, rocks, cover, way: S.way, mid: S.mid, mouth: S.door, giant, edges: S.edges.filter((h) => !cover.some((c) => c.q === h.q && c.r === h.r)) };
};

export interface AvalancheLayout {
  radius: number;
  rocks: Hex[];
  /** The void at the slope's sides, the cliff round the cave's mouth (for the drawing). */
  void: Hex[];
  cliff: Hex[];
  /** The lanes: the left edge of the first (x, sim units) and each one's width. */
  x0: number;
  laneW: number;
  /** The rows of rocks, from the top: each row's y, and its rocks (lane, grain, where it stands, its cell). */
  rows: { y: number; rocks: (LaneRock & { x: number; y: number; cell: Hex })[] }[];
  /** The one answer (rock indices, row by row) and where the slope came from in the table. */
  solution: number[];
  index: number;
  mirror: boolean;
  /** The cave's mouth (Kel and the stretcher inside it), where the hero starts, where the front starts and ends. */
  mouth: { x: number; y: number };
  kel: { x: number; y: number };
  start: { x: number; y: number };
  top: number;
  foot: number;
  /** The line the giant walks across (its two ends), the cells the wolves come over. */
  giant: [{ x: number; y: number }, { x: number; y: number }];
  edges: Hex[];
}

/** The rows of rocks (hex rows, from the top) and the cave's mouth row. */
const ROCK_ROWS = [-6, -2, 2];
const MOUTH_ROW = 8;

/** The avalanche's slope from the seed: one of the table's slopes (or its mirror), its rocks on their lanes. */
export const avalancheLayout = (seed: number): AvalancheLayout => {
  const R = AVALANCHE_RADIUS;
  const laneW = 1500;
  const x0 = -((LANES * laneW) >> 1);
  const W = bigWedges(seed, AVALANCHE_SLOPES);
  const rows = W.rows.map((row, i) => {
    const r = ROCK_ROWS[i] as number;
    const y = r * ROW;
    return {
      y,
      rocks: row.map((rk) => {
        const x = x0 + rk.lane * laneW + (laneW >> 1);
        return { ...rk, x, y, cell: worldToHex(x, y) };
      }),
    };
  });
  const rockK = new Set(rows.flatMap((r) => r.rocks.map((k) => key(k.cell))));
  const rocks: Hex[] = [];
  const voids: Hex[] = [];
  const cliff: Hex[] = [];
  const edges: Hex[] = [];
  const half = (LANES * laneW) >> 1;
  for (const h of allCells(R)) {
    const p = hexToWorld(h.q, h.r);
    if (rockK.has(key(h))) {
      rocks.push(h);
      continue;
    }
    // Off the slope's sides: the void.
    if (Math.abs(p.x) > half + 200) {
      rocks.push(h);
      voids.push(h);
      continue;
    }
    // Round the cave's mouth at the foot: the cliff the cave is cut into (the mouth itself three lanes wide).
    if (h.r >= MOUTH_ROW && Math.abs(p.x) > (laneW * 3) >> 1) {
      rocks.push(h);
      cliff.push(h);
      continue;
    }
    if (Math.abs(p.x) > half - 900 && h.r > -6 && h.r < 5) edges.push(h);
  }
  rocks.sort((a, b) => a.r - b.r || a.q - b.q);
  const mouthY = MOUTH_ROW * ROW;
  return {
    radius: R,
    rocks,
    void: voids,
    cliff,
    x0,
    laneW,
    rows,
    solution: W.solution,
    index: W.index,
    mirror: W.mirror,
    mouth: { x: 0, y: mouthY },
    kel: { x: 0, y: mouthY + 500 },
    start: { x: 0, y: (MOUTH_ROW - 2) * ROW },
    top: -(R + 1) * ROW,
    foot: mouthY,
    giant: [
      { x: x0 + laneW, y: -4 * ROW },
      { x: -x0 - laneW, y: -4 * ROW },
    ],
    edges,
  };
};

export interface CaveLayout {
  radius: number;
  rocks: Hex[];
  /** The floor (for the drawing), each room's middle and size, the door out of each (the way's cell shut by ice). */
  floor: Hex[];
  rooms: { at: Hex; r: number }[];
  doors: Hex[];
  /** Where the sentinels stand in each room (facing the way in), the hall of Fermín and Juana, the hero's start. */
  guards: Hex[][];
  hall: Hex;
  start: Hex;
}

/** The cells from a to b in a straight line (hex lerp). */
const line = (a: Hex, b: Hex): Hex[] => {
  const n = hexDist(a, b);
  const out: Hex[] = [];
  for (let i = 0; i <= n; i++) {
    // Axial lerp in thousandths of a hex, to the nearest cell.
    const q = a.q * 1000 + (n ? idiv((b.q - a.q) * 1000 * i, n) : 0);
    const r = a.r * 1000 + (n ? idiv((b.r - a.r) * 1000 * i, n) : 0);
    const w = { x: q + idiv(r, 2), y: idiv(r * ROW, 1000) };
    out.push(worldToHex(w.x, w.y));
  }
  return out;
};

/** The ice cave from the seed: the rooms' sideways shifts, how many sentinels where. */
export const caveLayout = (seed: number, counts: readonly number[] = [1, 2, 2]): CaveLayout => {
  const R = CAVE_RADIUS;
  const rng = seedRng(deriveSeed(seed, 0xca7e));
  const shift = (): number => rngRange(rng, -2, 2);
  // From the foot up: the way in, three rooms, the hall.
  const start: Hex = { q: -4, r: 8 };
  const centres: Hex[] = [];
  for (const r of [4, 0, -4]) {
    const q0 = -(r >> 1) + shift();
    centres.push({ q: q0, r });
  }
  const hall: Hex = { q: 4 + shift(), r: -8 };
  const rooms = centres.map((at, i) => ({ at, r: i === 0 ? 2 : 2 }));
  const open = new Set<string>();
  const floor: Hex[] = [];
  const dig = (h: Hex): void => {
    if (hexLen(h.q, h.r) > R || open.has(key(h))) return;
    open.add(key(h));
    floor.push(h);
  };
  for (const h of allCells(R)) {
    if (hexDist(h, start) <= 1) dig(h);
    if (hexDist(h, hall) <= 1) dig(h);
    for (const rm of rooms) if (hexDist(h, rm.at) <= rm.r) dig(h);
  }
  // The ways between: one cell wide; its middle cell is the door shut by ice.
  const stops = [start, ...centres, hall];
  const doors: Hex[] = [];
  for (let i = 0; i + 1 < stops.length; i++) {
    const way = line(stops[i] as Hex, stops[i + 1] as Hex);
    for (const h of way) dig(h);
    // The door: the first cell of the way outside the room it leaves (none out of the way in).
    if (i === 0) continue;
    const from = rooms[i - 1] as { at: Hex; r: number };
    const d = way.find((h) => hexDist(h, from.at) === from.r + 1) ?? (way[imin(way.length - 1, 3)] as Hex);
    doors.push(d);
  }
  // The sentinels: on the room's far half, a hex apart, facing down toward the way in.
  const guards = rooms.map((rm, i) => {
    const n = counts[i] ?? 1;
    const out: Hex[] = [];
    const spots = n === 1 ? [{ q: rm.at.q, r: rm.at.r - 1 }] : [{ q: rm.at.q - 1, r: rm.at.r - 1 }, { q: rm.at.q + 1, r: rm.at.r - 2 }, { q: rm.at.q, r: rm.at.r }];
    for (let k = 0; k < n; k++) out.push(spots[k % spots.length] as Hex);
    return out;
  });
  const rocks = allCells(R).filter((h) => !open.has(key(h)));
  rocks.sort((a, b) => a.r - b.r || a.q - b.q);
  return { radius: R, rocks, floor, rooms, doors, guards, hall, start };
};

/** The x2 of a cell (re-export for the drawing). */
export const slideCol = colOf;
